import type { AccountNumber } from "@@/shared/financials/accounting/account-number";
import type { AccountingData } from "@@/shared/financials/accounting/accounting-data";
import type { Price } from "@@/shared/prices/price";
import { subtractFromPrice } from "@@/shared/prices/subtract-from-price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { ProductId } from "@@/shared/products/product-id";
import { repeat } from "@@/utilities/repeat";
import type { Transaction } from "../transactions";
import { recalculateVat } from "./sie-generator-transaction-lines-vat";

function generateTransactionLines_SALES(
    transaction: Transaction,
    refund: number,
    accountingData: AccountingData,
) {
    const exclVatRows = transaction.items
        .filter(item => item.pricePerItem.amountIncludingVat !== 0)
        // .filter(item => !item.optionalId) //Remove optional from output as of bug #1060
        .map(item => {
            // For now, use the same account number always
            // as the account number for the product.

            switch (!!item.optionalId) {
                case true: {
                    // Item is an OPTIONAL
                    // Definition: #TRANS kontonr {objektlista} belopp transdat transtext kvantitet sign

                    const product = transaction.items
                        .filter(
                            transactionItem =>
                                item.orderItemId ===
                                    transactionItem.orderItemId &&
                                !transactionItem.optionalId,
                        )
                        .filter(Boolean);

                    if (product.length > 1) {
                        throw new Error("Optional parent couldn't be found");
                    }
                    const accountNumber = getAccountNumber(
                        product[0].productId,
                        accountingData,
                    );

                    const totalPrice = sumPrices(
                        repeat(item.pricePerItem, product[0].quantity), //Using product quantity as the other is 1
                    );

                    const dimensions = getDimensions(
                        product[0]?.productId,
                        accountNumber,
                        accountingData,
                    );

                    const line = sales_line(
                        totalPrice,
                        accountNumber,
                        refund,
                        transaction,
                        dimensions,
                    );
                    return line;
                }
                case false: {
                    let productId = item.productId;
                    if (!item.productId && item.parentItemId) {
                        const product = transaction.items.find(
                            transactionItem =>
                                item.parentItemId ===
                                transactionItem.orderItemId,
                        );
                        productId = product?.productId;
                    }

                    const accountNumber = getAccountNumber(
                        productId,
                        accountingData,
                    );

                    const dimensions = getDimensions(
                        productId,
                        accountNumber,
                        accountingData,
                    );

                    // Item must be a product or old fee
                    // Find optionals for item
                    const optionals = transaction.items
                        .filter(
                            transactionItem =>
                                item.orderItemId ===
                                    transactionItem.orderItemId &&
                                transactionItem.optionalId,
                        )
                        .filter(Boolean);

                    // Calculate price per product without optional pricing
                    const productPricePerItem = subtractFromPrice(
                        item.pricePerItem,
                        optionals.map(optional =>
                            sumPrices(
                                repeat(
                                    optional.pricePerItem,
                                    optional.quantity,
                                ),
                            ),
                        ),
                    );
                    const totalPrice = sumPrices(
                        repeat(productPricePerItem, item.quantity),
                    );
                    const line = sales_line(
                        totalPrice,
                        accountNumber,
                        refund,
                        transaction,
                        dimensions,
                    );
                    return line;
                }
            }
        });

    return exclVatRows;
}

const getDimensions = (
    productId: ProductId | undefined,
    accountNumber: AccountNumber,
    accountingData: AccountingData,
) => {
    const dimensions =
        accountingData.accountingDimensions?.flatMap(dimension => {
            const target = dimension.targets.find(target => {
                if (typeof target === "string") {
                    return target === accountNumber;
                }
                return target === productId;
            });
            if (!target) return [];
            return [
                {
                    dimId: dimension.id,
                    value: dimension.value,
                },
            ];
        }) ?? [];
    return dimensions;
};

function getAccountNumber(
    productId: ProductId | undefined,
    accountingData: AccountingData,
) {
    let accountNumber =
        (productId && accountingData.productAccountMapping[productId]) ||
        accountingData.fallbackProductAccount;
    if (
        !accountNumber ||
        (typeof accountNumber === "string" && !accountNumber.trim())
    ) {
        accountNumber = "SAKNAS" as AccountNumber;
    }
    return accountNumber;
}

function sales_line(
    totalPrice: Price,
    accountNumber: AccountNumber,
    refund: number,
    transaction: Transaction,
    dimensions: { dimId: string; value: string }[],
) {
    const price = recalculateVat(totalPrice);

    const dimensionString =
        dimensions.length > 0
            ? ` { ${dimensions
                  .map(d => `"${d.dimId}" "${d.value}"`)
                  .join(" ")} }`
            : "{ }";

    // Definition: #TRANS kontonr {objektlista} belopp transdat transtext kvantitet sign
    const line = `#TRANS ${accountNumber} ${dimensionString} ${
        (-1 * refund * price.amountExcludingVat) / 100
    } "" "${transaction.orderId}"`;
    return line;
}

export { generateTransactionLines_SALES, getDimensions };
